import React, { useState, ReactNode } from "react";
import { useHistory } from "react-router-dom";
import { Layout as SiteLayout, Menu, Grid, Row, Col, Button } from "antd";
import { useStore } from "../../store/useStore";
import { destroySession } from "../../store/actions";
import { LogoutOutlined } from "@ant-design/icons";
import { Cache } from "aws-amplify";
import { routes, AvailableApis } from "../../util/route-mapper/routes";
import SidebarMenuItemApi from "../../util/route-mapper/api/sidebar-menu-item-api";
import SidebarSubmenuApi from "../../util/route-mapper/api/sidebar-submenu-api";
import { AbstractRouteType } from "../../util/route-mapper";
import useRoleAccess from "../../hooks/useRoleAccess";
import "hamburgers";
import { Burger } from "../Burger/Burger";
import Title from "antd/lib/typography/Title";
import Text from "antd/lib/typography/Text";
import "./styles.scss";

const { Content, Sider } = SiteLayout;

type Props = {
  //content is a function that returns something of type ReactNode
  content: () => ReactNode;
};

/**
 * Persist the sidebar state during navigation
 * TODO maybe use local storage?
 */
type LocationState = {
  isCollapsed?: boolean;
};

const Sidebar: React.FC<Props> = ({ content }) => {
  const { state } = useStore();
  const history = useHistory<LocationState>();
  const [isCollapsed, setCollapsed] = useState<boolean>(
    !!Cache.getItem("sidebarCollapseState")
  );
  const [openMenus, setOpenMenus] = useState<string[]>(
    isCollapsed ? [] : Cache.getItem("openMenus") || []
  );
  const { dispatch } = useStore();
  const { hasRoles } = useRoleAccess();
  const screens = Grid.useBreakpoint();

  const handleLogout = async () => {
    try {
      await dispatch(destroySession());
      history.push("/login");
    } catch (error) { }
  };

  const mapMenus = (routes: AbstractRouteType[]): ReactNode[] => {
    return routes.map((route) => {
      if (route.isFeatureRestricted()) return null;

      const menuApi = route.getApi<AvailableApis, SidebarMenuItemApi>(
        "sidebar-menu-item-api"
      );
      if (menuApi) {
        return menuApi.use({ history, hasRoles });
      }
      const submenuApi = route.getApi<AvailableApis, SidebarSubmenuApi>(
        "sidebar-submenu-api"
      );
      if (submenuApi) {
        return submenuApi.use({
          state,
          history,
          hasRoles,
          onTitleClick: (owningRoute) => {
            let newOpenMenus = [];
            if (openMenus.includes(owningRoute.path as string))
              newOpenMenus = openMenus.filter((key) => key !== route.path);
            else newOpenMenus = [...openMenus, owningRoute.path as string];
            Cache.setItem("openMenus", newOpenMenus);
            setOpenMenus(newOpenMenus);
          },
        });
      }
      return null;
    });
  };

  const mappedMenus = mapMenus(routes)
    .flat(5)
    .filter((menu) => menu !== undefined && menu !== null);

  return (
    <div>
      <SiteLayout>
        <SiteLayout.Header
          style={{ position: "fixed", zIndex: 1, width: "100%" }}
        >
          <Row align="middle" style={{ height: 64 }} justify="space-between">
            <Col>
              <img
                alt="iLivestock Logo"
                className={
                  screens.xs ? "ilivestock-logo" : "full-ilivestock-logo"
                }
                style={{
                  marginLeft: screens.xs
                    ? "25px"
                    : "-15px",
                }}
                src={`https://ilivestock.co.uk/dist/images/navigation-bar/${screens.xs ? "logo-smaller.png" : "full-ilivestock-logo.png"
                  }`}
              ></img>
            </Col>
            <Row>
              <Col>
                <Text style={{ color: "white", margin: "0px 8px" }}>
                  Welcome, {state.user?.attributes.given_name}
                </Text>
              </Col>
              <Col>
                <Button
                  size="middle"
                  shape="circle"
                  onClick={() => {
                    handleLogout();
                  }}
                  icon={
                    <LogoutOutlined
                      style={{ fontSize: 18, transform: "translateY(-3px)" }}
                    />
                  }
                ></Button>
              </Col>
            </Row>
          </Row>
        </SiteLayout.Header>
        <SiteLayout>
          <Sider
            theme="light"
            style={{
              overflow: "auto",
              height: "100vh",
              position: "fixed",
              left: 0,
              width: 256,
              marginTop: 64,
              zIndex: 1,
            }}
            trigger={null}
            breakpoint="sm"
            collapsible
            collapsedWidth={screens.sm ? undefined : 0}
            onCollapse={() => {
              setCollapsed(!isCollapsed);
              Cache.setItem("sidebarCollapseState", !isCollapsed);
            }}
            collapsed={!screens.sm && isCollapsed}
          >
            <Menu
              theme="light"
              mode="inline"
              defaultSelectedKeys={[history.location.pathname]}
              openKeys={openMenus}
              style={{ minHeight: "100vh" }}
            >
              {mappedMenus}
            </Menu>
          </Sider>
        </SiteLayout>
        <SiteLayout
          className="site-layout"
        // style={{ marginLeft: isCollapsed ? 75 : 200, minHeight: "100vh" }}
        >
          {screens.xs && (
            <Burger
              onToggle={(open) => {
                setCollapsed(!open);
              }}
            />
          )}
          <Content
            style={{
              padding: !screens.sm && isCollapsed ? "32px 32px" : "32px 48px",
              overflow: "initial",
              background: "white",
              marginLeft: !screens.sm && isCollapsed ? 0 : 200,
              paddingTop: 92,
            }}
          >
            {content()}
          </Content>
        </SiteLayout>
      </SiteLayout>
    </div>
  );
};

export default Sidebar;
